import { useComponentLoadingStateStore } from "../../store/useComponentLoadingStateStore";
import { useRouterStore } from "../../store/useRouterStore";
import { Button, Slider, Typography } from "@mui/material";
import { useWindowSize } from "../../hooks/useWindowSize";
import { SimpleBox } from "../MaterialCustomComponents";
import LeftArrowIcon from "./LeftArrowIcon";
import MapIconSVG from "./MapIconSVG";
import RightArrowIcon from "./RightArrowIcon";
import Image from "next/image";
import { LEFT, RIGHT } from "../../configs/ARROWS_DIRECTION_CONFIG";
import { EXHIBITORS_MAP } from "../../configs/NAVIGATION_PAGES";
import BackButtonSVG from "./BackButtonSVG";
import { debounce } from "../../helpers/debounce";
import { useEffect, useState } from "react";
import { arrowDirection } from "../../store/interfaces/IRouterStore";
import { openStripes } from "../../animations/animateStripes";
import { updateRoute } from "../../helpers/updateBoothURL";
import { findBoothIndex } from "../../helpers/updateNextAndPreviousBoothURL";
import { useModelStore } from "../../store/useModelStore";
import { useRouter } from "next/router";
import { closeAllNavMenus } from "../../helpers/closeAllNavMenus";
import BoothInfoComponent from "./BoothInfoComponent";

const ControlPanelDesktop = () => {
  const {
    setIsSingleBoothViewButtonPressed,
    setIsInfoCategoryClicked,
    setHallZoomValue,
    isBoothVisitPartActive,
    setIsVideoClicked,
    setIsSideNavOpen,
    setIsMenuExpanded,
    setIsBurgerOpen,
    hallZoomValue,
    isExpoPageLoaded,
    setIsCameraZoomEnabled,
    setIsBoothContactFormOpen,
    setIsVideoCategoryClicked,
  } = useComponentLoadingStateStore();
  const { setArrowDirection, newTrigger, hallID, boothID, setActiveCategory } =
    useRouterStore();
  const { booths } = useModelStore();
  const { height } = useWindowSize();
  const [isControlPanelDisabled, setIsControlPanelDisabled] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const { query } = useRouter();

  useEffect(() => {
    if (isExpoPageLoaded) {
    } else {
      setIsCameraZoomEnabled(false);
    }
    setIsControlPanelDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpoPageLoaded]);

  const handleChangeButtons = (direction: arrowDirection) => {
    setIsControlPanelDisabled(true);
    setIsCameraZoomEnabled(true);
    setHallZoomValue(0);
    setIsVideoClicked(false);
    setIsInfoCategoryClicked("");
    setArrowDirection(direction);
    newTrigger();
  };

  const handleOpenMapClick = () => {
    setIsSideNavOpen(true);
    setIsMenuExpanded(true);
    setActiveCategory(EXHIBITORS_MAP);
    setIsBurgerOpen(true);
  };

  const handleBackButtonClick = () => {
    setIsVideoCategoryClicked(false);
    setIsVideoClicked(false);
    setIsInfoOpen(false);
    if (isBoothVisitPartActive) {
      setIsSingleBoothViewButtonPressed(true);
      return;
    }
    updateRoute(`/expo/halls/${query["hall-ID"]}/booths`);
    setHallZoomValue(0);
    setIsVideoClicked(false);
  };

  const handleInfoClick = (category: string | undefined = undefined) => {
    closeAllNavMenus();
    if (category === "contact") {
      setIsBoothContactFormOpen(true);
      return;
    }
    if (category === "brochures") {
      setIsVideoCategoryClicked(false);
    }
    if (category === undefined) {
      setIsInfoCategoryClicked("");
      setIsInfoOpen(!isInfoOpen);
      return;
    }
    setIsInfoCategoryClicked(category);
  };

  const handleZoomChange = (e: any) => {
    setHallZoomValue(e.target.value);
  };

  const splitBoothID = (booth: string, direction: string) => {
    if (boothID === "") {
      return true;
    }
    if (direction === "left") {
      const hasBoothToTheLeft = findBoothIndex(booth, booths, "left");
      return hasBoothToTheLeft ? true : false;
    }
    if (direction === "right") {
      const hasBoothToTheRight = findBoothIndex(booth, booths, "right");
      return hasBoothToTheRight ? true : false;
    }
  };

  return (
    <SimpleBox
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: typeof window !== "undefined" && height,
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
        alignItems: "end",
        paddingBottom: "1rem",
      }}
    >
      <SimpleBox
        sx={{
          backgroundColor: "black",
          border: "3px solid white",
          padding: "0.5rem",
          display: "flex",
          gap: "1rem",
        }}
      >
        {!isBoothVisitPartActive && (
          <Button
            variant="controlPanelDesktop"
            onClick={() => handleChangeButtons(LEFT)}
            disabled={
              isControlPanelDisabled ||
              (splitBoothID(boothID, "left") ? false : true) ||
              (Number(hallID) === 1 && boothID === "" ? true : false)
            }
          >
            <SimpleBox>
              <LeftArrowIcon width={20} height={20} strokeWidth={3} />
            </SimpleBox>
            <Typography sx={{ color: "#ffffff", fontSize: "0.7rem" }}>
              {boothID === "" ? "Previous Hall" : "Previous Booth"}
            </Typography>
          </Button>
        )}
        {boothID !== "" && (
          <Button
            variant="controlPanelDesktop"
            onClick={debounce(handleBackButtonClick)}
          >
            <SimpleBox>
              <BackButtonSVG />
            </SimpleBox>
            <Typography sx={{ color: "#ffffff", fontSize: "0.7rem" }}>
              Go Back
            </Typography>
          </Button>
        )}
        {boothID !== "" && (
          <SimpleBox>
            <BoothInfoComponent
              isInfoOpen={isInfoOpen}
              handleInfoClick={handleInfoClick}
            />
          </SimpleBox>
        )}
        {boothID === "" && (
          <>
            <Button
              variant="controlPanelDesktop"
              onClick={() => handleOpenMapClick()}
              disabled={isControlPanelDisabled ? true : false}
            >
              <MapIconSVG />
              <Typography sx={{ color: "#ffffff", fontSize: "0.7rem" }}>
                Booths Map
              </Typography>
            </Button>
            <SimpleBox
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <SimpleBox
                sx={{
                  pointerEvents: "auto",
                  backgroundColor: "rgba(0, 0, 0)",
                  width: "10rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  opacity: isControlPanelDisabled ? 0.5 : 1,
                }}
              >
                <Image
                  src="/zoom.png"
                  alt="map icon"
                  width={30}
                  height={30}
                  style={{ pointerEvents: "auto" }}
                />
                <Slider
                  disabled={isControlPanelDisabled ? true : false}
                  defaultValue={0}
                  aria-label="Default"
                  valueLabelDisplay="auto"
                  value={isControlPanelDisabled ? 0 : hallZoomValue}
                  marks={[
                    { value: 0 },
                    { value: 25 },
                    { value: 50 },
                    { value: 75 },
                    { value: 100 },
                  ]}
                  step={25}
                  onChange={(e) => handleZoomChange(e)}
                />
              </SimpleBox>
              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: "0.8rem",
                  opacity: isControlPanelDisabled ? 0.5 : 1,
                }}
              >
                Hall Zoom
              </Typography>
            </SimpleBox>
          </>
        )}
        {!isBoothVisitPartActive && (
          <Button
            variant="controlPanelDesktop"
            onClick={
              Number(hallID) === 6 && boothID === ""
                ? () => {
                    openStripes(() => {
                      updateRoute("/conference_hall");
                    });
                  }
                : () => handleChangeButtons(RIGHT)
            }
            disabled={
              isControlPanelDisabled ||
              (splitBoothID(boothID, "right") ? false : true)
            }
          >
            <SimpleBox>
              <RightArrowIcon width={20} height={20} strokeWidth={3} />
            </SimpleBox>
            {boothID === "" && (
              <Typography sx={{ color: "#ffffff", fontSize: "0.7rem" }}>
                {Number(hallID) === 6 && boothID === ""
                  ? "Conference Hall"
                  : "Next Hall"}
              </Typography>
            )}
            {boothID !== "" && (
              <Typography sx={{ color: "#ffffff", fontSize: "0.7rem" }}>
                Next Booth
              </Typography>
            )}
          </Button>
        )}
      </SimpleBox>
    </SimpleBox>
  );
};

export default ControlPanelDesktop;
